/* ==========================================================================
   GMJ Museum theme – interactions
   Gate · reveals & gallery lighting · torch · wayfinding · long gallery ·
   vitrines · cartouche scribe · cart · product exhibit · search · filters
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var html = d.documentElement;
  var GMJ = window.GMJ || {};
  GMJ.routes = GMJ.routes || {};
  GMJ.settings = GMJ.settings || {};
  GMJ.strings = GMJ.strings || {};

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var isRTL = html.dir === 'rtl';

  /* ---------- helpers ---------- */
  function qs(sel, root) { return (root || d).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); }
  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, self = this; clearTimeout(t); t = setTimeout(function () { fn.apply(self, a); }, ms); }; }
  function inView(el, margin) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight;
    return r.top < vh * (margin || 1) && r.bottom > 0 && (r.width > 0 || r.height > 0);
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    format = format || GMJ.moneyFormat || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(n, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(n)) return '0';
      var parts = (n / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(placeholder);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* One requestAnimationFrame loop for everything scroll-driven */
  var scrollFns = [];
  var ticking = false;
  function onScroll(fn) { scrollFns.push(fn); }
  function tick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      for (var i = 0; i < scrollFns.length; i++) scrollFns[i]();
    });
  }
  window.addEventListener('scroll', tick, { passive: true });
  window.addEventListener('resize', tick);

  /* ---------- toast ---------- */
  var toastTimer;
  function toast(msgHtml) {
    var t = d.getElementById('Toast');
    if (!t) return;
    qs('[data-toast-msg]', t).innerHTML = msgHtml;
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-on'); }, 2800);
  }

  /* ==========================================================================
     Entrance gate
     ========================================================================== */
  function initGate() {
    var gate = qs('[data-gate]');
    if (!gate) return;
    if (!html.classList.contains('gate-active')) { gate.classList.add('is-gone'); return; }

    var opened = false;
    function open() {
      if (opened) return;
      opened = true;
      try { sessionStorage.setItem('gmj-gate', '1'); } catch (e) { /* private mode */ }
      gate.classList.add('is-open');
      setTimeout(function () {
        html.classList.remove('gate-active');
        window.dispatchEvent(new CustomEvent('gmj:entered'));
        tick();
      }, reduce.matches ? 0 : 650);
      setTimeout(function () { gate.classList.add('is-fading'); }, reduce.matches ? 0 : 1500);
      setTimeout(function () { gate.classList.add('is-gone'); }, reduce.matches ? 0 : 2300);
      d.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }

    if (reduce.matches) { open(); return; }

    var btn = qs('[data-gate-enter]', gate);
    if (btn) {
      btn.addEventListener('click', open);
      try { btn.focus({ preventScroll: true }); } catch (e) { btn.focus(); }
    }
    qsa('.gate__door', gate).forEach(function (door) { door.addEventListener('click', open); });
    d.addEventListener('keydown', onKey);
    gate.addEventListener('wheel', open, { passive: true });
    gate.addEventListener('touchmove', open, { passive: true });

    var auto = Number(GMJ.settings.gateAutoplay || 0);
    if (auto > 0) setTimeout(open, auto * 1000);
  }

  /* ==========================================================================
     Reveals, gallery lighting, split headings
     ========================================================================== */
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      var el = en.target;
      revealIO.unobserve(el);
      el.classList.add('is-revealing', 'is-in');
      el.classList.remove('reveal-pending');
      var i = parseFloat(getComputedStyle(el).getPropertyValue('--i')) || 0;
      setTimeout(function () { el.classList.remove('is-revealing'); }, 2300 + i * 90);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }) : null;

  function initReveals(root) {
    qsa('[data-reveal]', root).forEach(function (el) {
      if (el.classList.contains('is-in') || el.classList.contains('reveal-pending')) return;
      if (!revealIO || reduce.matches || inView(el, 0.96)) { el.classList.add('is-in'); return; }
      el.classList.add('reveal-pending');
      revealIO.observe(el);
    });
  }

  var lightIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      en.target.classList.add('is-lit');
      lightIO.unobserve(en.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }) : null;

  function initLights(root) {
    if (!d.body.classList.contains('has-lights')) return;
    qsa('.exhibit-light', root).forEach(function (img) {
      if (img.classList.contains('is-lit')) return;
      if (!lightIO || reduce.matches || inView(img)) { img.classList.add('is-lit'); return; }
      lightIO.observe(img);
    });
    html.classList.add('lights-ready');
  }

  function splitHeading(el) {
    if (el.getAttribute('data-split-done')) return;
    el.setAttribute('data-split-done', '1');
    // Each word becomes an inline-block, so let the heading's own language set its direction
    if (!el.hasAttribute('dir')) el.setAttribute('dir', 'auto');
    var i = 0;
    function wrap(node) {
      var outer = d.createElement('span');
      outer.className = 'sw';
      var inner = d.createElement('span');
      inner.className = 'sw__i';
      inner.style.setProperty('--wi', i++);
      outer.appendChild(inner);
      inner.appendChild(node);
      return outer;
    }
    Array.prototype.slice.call(el.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var frag = d.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) frag.appendChild(d.createTextNode(' '));
          else frag.appendChild(wrap(d.createTextNode(part)));
        });
        el.replaceChild(frag, child);
      } else if (child.nodeType === 1) {
        var placeholder = d.createTextNode('');
        el.replaceChild(placeholder, child);
        el.replaceChild(wrap(child), placeholder);
      }
    });
  }

  var splitIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      en.target.classList.remove('split-pending');
      splitIO.unobserve(en.target);
    });
  }, { rootMargin: '0px 0px -10% 0px' }) : null;

  function initSplits(root) {
    if (!GMJ.settings.splitHeadings || reduce.matches || !splitIO) return;
    qsa('[data-split]', root).forEach(function (el) {
      if (el.getAttribute('data-split-done')) return;
      var visible = inView(el);
      splitHeading(el);
      if (!visible) {
        el.classList.add('split-pending');
        splitIO.observe(el);
      }
    });
  }

  /* ==========================================================================
     Torch: a soft light that follows the visitor
     ========================================================================== */
  function initTorch() {
    var torch = qs('.torch');
    if (!torch) return;
    if (!finePointer.matches || reduce.matches) { torch.remove(); return; }
    var tx = window.innerWidth / 2, ty = window.innerHeight * 0.4, x = tx, y = ty, raf = 0;
    function loop() {
      x += (tx - x) * 0.11;
      y += (ty - y) * 0.11;
      torch.style.setProperty('--tx', x.toFixed(1) + 'px');
      torch.style.setProperty('--ty', y.toFixed(1) + 'px');
      raf = (Math.abs(tx - x) > 0.4 || Math.abs(ty - y) > 0.4) ? requestAnimationFrame(loop) : 0;
    }
    window.addEventListener('pointermove', function (e) {
      tx = e.clientX;
      ty = e.clientY;
      if (!html.classList.contains('gate-active')) torch.classList.add('is-on');
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    html.addEventListener('mouseleave', function () { torch.classList.remove('is-on'); });
  }

  /* ==========================================================================
     Floating dust in the hero light beam
     ========================================================================== */
  function initDust(root) {
    if (reduce.matches) return;
    qsa('[data-dust]', root).forEach(function (canvas) {
      if (canvas.getAttribute('data-on')) return;
      canvas.setAttribute('data-on', '1');
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var color = getComputedStyle(html).getPropertyValue('--gold-hi-rgb').trim() || '243, 220, 149';
      var parts = [], w = 0, h = 0, raf = 0, running = false;

      function spawn(anywhere) {
        return {
          x: Math.random() * w,
          y: anywhere ? Math.random() * h : h + 8,
          r: Math.random() * 1.5 + 0.35,
          vy: -(Math.random() * 0.22 + 0.05),
          vx: (Math.random() - 0.5) * 0.1,
          a: Math.random() * 0.5 + 0.15,
          t: Math.random() * Math.PI * 2
        };
      }
      function size() {
        var r = canvas.getBoundingClientRect();
        w = r.width; h = r.height;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        var n = Math.round(clamp((w * h) / 20000, 20, 70));
        parts = [];
        for (var i = 0; i < n; i++) parts.push(spawn(true));
      }
      function frame() {
        ctx.clearRect(0, 0, w, h);
        for (var i = 0; i < parts.length; i++) {
          var p = parts[i];
          p.t += 0.012;
          p.x += p.vx + Math.sin(p.t) * 0.08;
          p.y += p.vy;
          if (p.y < -8) { parts[i] = spawn(false); continue; }
          var alpha = p.a * (0.55 + Math.sin(p.t * 3) * 0.45);
          ctx.beginPath();
          ctx.fillStyle = 'rgba(' + color + ',' + alpha.toFixed(3) + ')';
          ctx.arc(p.x, p.y, p.r, 0, 6.2832);
          ctx.fill();
        }
        raf = requestAnimationFrame(frame);
      }
      size();
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          if (!running) { running = true; frame(); }
        } else {
          running = false;
          cancelAnimationFrame(raf);
        }
      }).observe(canvas);
      window.addEventListener('resize', debounce(size, 200));
    });
  }

  /* ==========================================================================
     Parallax & tilt
     ========================================================================== */
  function initParallax(root) {
    if (reduce.matches) return;
    var els = qsa('[data-parallax]', root);
    if (!els.length) return;
    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        if (!el.isConnected) return;
        var prev = parseFloat(el.getAttribute('data-py')) || 0;
        var r = el.getBoundingClientRect();
        var top = r.top - prev;
        if (top > vh + 200 || top + r.height < -200) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0;
        var y = -((top + r.height / 2) - vh / 2) * speed;
        el.setAttribute('data-py', y.toFixed(1));
        el.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
      });
    }
    onScroll(update);
    update();
  }

  function initTilt(root) {
    if (!finePointer.matches || reduce.matches) return;
    qsa('[data-tilt]', root).forEach(function (el) {
      if (el.getAttribute('data-tilt-on')) return;
      el.setAttribute('data-tilt-on', '1');
      var max = parseFloat(el.getAttribute('data-tilt')) || 4;
      var target = el.classList.contains('vitrine') ? qs('.vitrine__case', el) : el;
      if (!target) return;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        target.style.setProperty('--rx', (px * max * 2).toFixed(2) + 'deg');
        target.style.setProperty('--ry', (-py * max * 2).toFixed(2) + 'deg');
        target.style.setProperty('--gx', ((px + 0.5) * 100).toFixed(1) + '%');
        target.style.setProperty('--gy', ((py + 0.5) * 100).toFixed(1) + '%');
      });
      el.addEventListener('pointerleave', function () {
        target.style.setProperty('--rx', '0deg');
        target.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* ==========================================================================
     Wayfinding sign: which room you are in
     ========================================================================== */
  var NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
  var rooms = [];
  function collectRooms() {
    rooms = qsa('[data-room]').filter(function (el) { return (el.getAttribute('data-room') || '').trim(); });
  }
  function initRooms() {
    var sign = qs('.room-sign');
    if (!sign) return;
    collectRooms();
    var noEl = qs('[data-room-no]', sign);
    var nameEl = qs('[data-room-name]', sign);
    var current = null;
    function set(el) {
      if (el === current) return;
      current = el;
      var idx = rooms.indexOf(el);
      noEl.textContent = NUMERALS[idx] || String(idx + 1);
      nameEl.textContent = el.getAttribute('data-room');
      sign.classList.remove('is-changing');
      void sign.offsetWidth;
      sign.classList.add('is-changing');
    }
    function update() {
      var mid = window.innerHeight * 0.45;
      var pick = null;
      for (var i = 0; i < rooms.length; i++) {
        var r = rooms[i].getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) { pick = rooms[i]; break; }
      }
      if (pick) set(pick);
      var max = html.scrollHeight - window.innerHeight;
      sign.style.setProperty('--p', (max > 0 ? window.scrollY / max : 0).toFixed(3));
      sign.classList.toggle('is-on', !!pick && window.scrollY > window.innerHeight * 0.4 && !html.classList.contains('gate-active'));
    }
    onScroll(update);
    update();
  }

  /* ==========================================================================
     Long gallery: pinned horizontal walk with lamps lighting each piece
     ========================================================================== */
  function initWalk(root) {
    qsa('[data-walk]', root).forEach(function (sec) {
      if (sec.getAttribute('data-walk-on')) return;
      sec.setAttribute('data-walk-on', '1');
      var track = qs('[data-walk-track]', sec);
      var bar = qs('[data-walk-progress]', sec);
      var exhibits = qsa('[data-exhibit]', sec);
      var wide = window.matchMedia('(min-width: 900px)');
      var dist = 0;
      var pinned = false;

      function update() {
        if (!pinned || !sec.isConnected) return;
        var r = sec.getBoundingClientRect();
        var total = r.height - window.innerHeight;
        var p = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
        track.style.transform = 'translate3d(' + (p * dist * (isRTL ? 1 : -1)).toFixed(1) + 'px,0,0)';
        if (bar) bar.parentNode.style.setProperty('--p', p.toFixed(3));
        var cx = window.innerWidth / 2;
        var span = window.innerWidth * 0.55;
        exhibits.forEach(function (ex) {
          var er = ex.getBoundingClientRect();
          var c = clamp(1 - Math.abs((er.left + er.width / 2 - cx) / span), 0, 1);
          c = c * c * (3 - 2 * c);
          ex.style.setProperty('--lit', (0.22 + c * 0.78).toFixed(3));
        });
      }
      function measure() {
        pinned = wide.matches && !reduce.matches;
        sec.classList.toggle('is-pinned', pinned);
        if (!pinned) {
          sec.style.height = '';
          track.style.transform = '';
          exhibits.forEach(function (ex) { ex.style.removeProperty('--lit'); });
          return;
        }
        dist = Math.max(0, track.scrollWidth - window.innerWidth);
        sec.style.height = (window.innerHeight + dist) + 'px';
        update();
      }
      onScroll(update);
      window.addEventListener('resize', debounce(measure, 150));
      window.addEventListener('load', measure);
      measure();
    });
  }

  /* ==========================================================================
     Swipe rows on touch screens: hint, progress line and a one-time peek
     ========================================================================== */
  function initSwipe(root) {
    qsa('[data-swipe]', root).forEach(function (row) {
      if (row.getAttribute('data-swipe-on')) return;
      row.setAttribute('data-swipe-on', '1');
      var scope = row.closest('section') || row.parentNode;
      var hint = qs('[data-swipe-hint]', scope);
      var bar = qs('[data-swipe-bar]', scope);
      var done = false;

      function update() {
        var max = row.scrollWidth - row.clientWidth;
        if (max <= 2) return;
        var x = Math.abs(row.scrollLeft);
        if (bar) {
          var w = Math.max(0.14, row.clientWidth / row.scrollWidth);
          var track = bar.parentNode.clientWidth;
          bar.style.setProperty('--sw', (w * 100).toFixed(1) + '%');
          bar.style.setProperty('--sx', ((x / max) * (1 - w) * track * (isRTL ? -1 : 1)).toFixed(1) + 'px');
        }
        if (!done && x > 24 && hint) {
          done = true;
          hint.classList.add('is-done');
        }
      }
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();

      if (reduce.matches || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        if (row.scrollWidth - row.clientWidth > 2 && Math.abs(row.scrollLeft) < 4) {
          row.classList.add('is-peeking');
          setTimeout(function () { row.classList.remove('is-peeking'); }, 1800);
        }
      }, { threshold: 0.6 });
      io.observe(row);
    });
  }

  /* ==========================================================================
     Header, directory and overlays
     ========================================================================== */
  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;
    var wrap = header.closest('.shopify-section') || header.parentNode;
    var mode = header.getAttribute('data-sticky');
    wrap.classList.toggle('no-sticky', mode === 'none');
    var last = window.scrollY;
    if (mode === 'scroll-up') {
      onScroll(function () {
        var y = window.scrollY;
        if (Math.abs(y - last) < 6) return;
        wrap.classList.toggle('is-hidden', y > last && y > 260 && !openOverlay);
        last = y;
      });
    }
    function setH() { html.style.setProperty('--header-h', header.offsetHeight + 'px'); }
    setH();
    window.addEventListener('resize', debounce(setH, 150));

    // Directory: hovering a hall lights its room on the floor plan
    qsa('.directory__hall').forEach(function (hall) {
      var cell = qs('[data-room-cell="' + hall.getAttribute('data-hall') + '"]');
      function on() { if (cell) cell.classList.add('is-hot'); hall.classList.add('is-hot'); }
      function off() { if (cell) cell.classList.remove('is-hot'); hall.classList.remove('is-hot'); }
      hall.addEventListener('mouseenter', on);
      hall.addEventListener('mouseleave', off);
      hall.addEventListener('focusin', on);
      hall.addEventListener('focusout', off);
    });
  }

  var openOverlay = null;
  var lastFocus = null;
  function openOv(ov, trigger) {
    if (!ov) return;
    if (openOverlay && openOverlay !== ov) closeOv(openOverlay, true);
    lastFocus = trigger || d.activeElement;
    ov.hidden = false;
    void ov.offsetWidth;
    ov.classList.add('is-open');
    if (ov.id === 'CartDrawer') {
      ov.classList.add('is-opening');
      setTimeout(function () { ov.classList.remove('is-opening'); }, 1400);
    }
    openOverlay = ov;
    html.classList.add('overlay-lock');
    qsa('[aria-controls="' + ov.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var focusEl = qs('[data-predictive-input]', ov) || qs('[data-overlay-close].icon-btn', ov);
    if (focusEl) setTimeout(function () { try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); } }, 60);
  }
  function closeOv(ov, quiet) {
    if (!ov) return;
    ov.classList.remove('is-open');
    qsa('[aria-controls="' + ov.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (openOverlay === ov) openOverlay = null;
    html.classList.remove('overlay-lock');
    setTimeout(function () { if (!ov.classList.contains('is-open')) ov.hidden = true; }, quiet ? 0 : 650);
    if (!quiet && lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
  }
  function trapFocus(e) {
    var items = qsa('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', openOverlay)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ==========================================================================
     Cart
     ========================================================================== */
  function updateCount(n) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
      if (n > 0) {
        el.classList.add('bump');
        setTimeout(function () { el.classList.remove('bump'); }, 360);
      }
    });
  }
  function applyDrawer(sections) {
    if (!sections || !sections['cart-drawer']) return;
    var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    var fresh = qs('[data-cart-drawer-inner]', doc);
    var cur = qs('[data-cart-drawer-inner]');
    if (fresh && cur) cur.innerHTML = fresh.innerHTML;
  }
  function addToCart(payload) {
    var isForm = payload instanceof FormData;
    if (isForm) {
      payload.append('sections', 'cart-drawer');
      payload.append('sections_url', window.location.pathname);
    } else {
      payload.sections = 'cart-drawer';
      payload.sections_url = window.location.pathname;
    }
    return fetch(GMJ.routes.cartAdd + '.js', {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? payload : JSON.stringify(payload)
    })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw j; return j; }); })
      .then(function (j) {
        applyDrawer(j.sections);
        return fetch(GMJ.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) { updateCount(cart.item_count); return cart; });
  }
  function afterAdd(title) {
    var mode = GMJ.settings.cartType;
    if (mode === 'page') { window.location.href = GMJ.routes.cart; return; }
    var drawer = d.getElementById('CartDrawer');
    if (mode === 'drawer' && drawer) openOv(drawer);
    else toast('<b>' + esc(title) + '</b> ' + esc(GMJ.strings.added || 'added to your bag'));
  }
  function cartError(err) {
    toast(esc((err && (err.description || err.message)) || GMJ.strings.error || 'Something went wrong'));
  }
  function markDone(btn) {
    if (!btn) return;
    var label = qs('span', btn) || btn;
    var old = label.textContent;
    btn.classList.add('is-done');
    label.textContent = '✓';
    setTimeout(function () { btn.classList.remove('is-done'); label.textContent = old; }, 1600);
  }

  function changeLine(line, qty) {
    var inner = qs('[data-cart-drawer-inner]');
    if (inner) inner.classList.add('is-loading');
    return fetch(GMJ.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, sections: 'cart-drawer', sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.status) throw cart;
        applyDrawer(cart.sections);
        updateCount(cart.item_count);
      })
      .catch(cartError)
      .then(function () {
        var i = qs('[data-cart-drawer-inner]');
        if (i) i.classList.remove('is-loading');
      });
  }

  /* ==========================================================================
     Product exhibit: variants, media, loupe
     ========================================================================== */
  function initLoupe(sec) {
    if (!finePointer.matches) return;
    qsa('[data-loupe]', sec).forEach(function (wrap) {
      var lens = qs('.loupe', wrap);
      var img = qs('img', wrap);
      if (!lens || !img) return;
      var zoom = 2.6, loaded = false;
      wrap.addEventListener('pointerenter', function () {
        if (!loaded) { lens.style.backgroundImage = 'url("' + wrap.getAttribute('data-loupe') + '")'; loaded = true; }
        wrap.classList.add('is-zoom');
      });
      wrap.addEventListener('pointerleave', function () { wrap.classList.remove('is-zoom'); });
      wrap.addEventListener('pointermove', function (e) {
        var r = wrap.getBoundingClientRect();
        var x = e.clientX - r.left, y = e.clientY - r.top;
        var nw = img.naturalWidth || r.width, nh = img.naturalHeight || r.height;
        var scale = Math.max(r.width / nw, r.height / nh);
        var dw = nw * scale, dh = nh * scale;
        var ox = (r.width - dw) / 2, oy = (r.height - dh) / 2;
        var half = lens.offsetWidth / 2;
        lens.style.left = x + 'px';
        lens.style.top = y + 'px';
        lens.style.backgroundSize = (dw * zoom) + 'px ' + (dh * zoom) + 'px';
        lens.style.backgroundPosition = (-((x - ox) * zoom - half)) + 'px ' + (-((y - oy) * zoom - half)) + 'px';
      });
    });
  }

  function initProduct(root) {
    qsa('[data-product]', root).forEach(function (sec) {
      if (sec.getAttribute('data-product-on')) return;
      sec.setAttribute('data-product-on', '1');
      var json = qs('[data-product-json]', sec);
      if (!json) return;
      var product;
      try { product = JSON.parse(json.textContent); } catch (e) { return; }
      var picker = qs('[data-variant-picker]', sec);
      var idInput = qs('[data-variant-id]', sec);
      var addBtn = qs('[data-add-btn]', sec);
      var price = qs('[data-price-wrap] [data-price]', sec);

      function showMedia(id) {
        if (!id) return;
        var target = qs('.pdp__media[data-media-id="' + id + '"]', sec);
        if (!target) return;
        qsa('.pdp__media', sec).forEach(function (m) { m.classList.toggle('is-active', m === target); });
        qsa('[data-thumb]', sec).forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-thumb') === String(id)); });
        qsa('.pdp__media:not(.is-active) video', sec).forEach(function (v) { v.pause(); });
      }
      qsa('[data-thumb]', sec).forEach(function (t) {
        t.addEventListener('click', function () { showMedia(t.getAttribute('data-thumb')); });
      });

      // Touch: swipe across the vitrine to see the next view of the piece
      var stage = qs('[data-pdp-stage]', sec);
      var mediaIds = qsa('.pdp__media[data-media-id]', sec).map(function (m) { return m.getAttribute('data-media-id'); });
      if (stage && mediaIds.length > 1) {
        var sx = 0, sy = 0;
        stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          var dx = e.changedTouches[0].clientX - sx;
          var dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          var cur = qs('.pdp__media.is-active', sec);
          var idx = Math.max(0, mediaIds.indexOf(cur ? cur.getAttribute('data-media-id') : ''));
          var forward = isRTL ? dx > 0 : dx < 0;
          showMedia(mediaIds[(idx + (forward ? 1 : -1) + mediaIds.length) % mediaIds.length]);
        }, { passive: true });
      }

      // Phones: a slim add-to-bag bar once the main button has scrolled away
      var sticky = (sec.closest('.shopify-section') || sec.parentNode).querySelector('[data-pdp-sticky]');
      var stickyBtn = sticky && qs('[data-sticky-add]', sticky);
      if (sticky && addBtn && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var en = entries[0];
          var on = !en.isIntersecting && en.boundingClientRect.top < 0;
          sticky.classList.toggle('is-on', on);
          d.body.classList.toggle('has-sticky-bar', on);
        }).observe(addBtn);
        stickyBtn.addEventListener('click', function () {
          var form = addBtn.form;
          if (!form) return;
          if (form.requestSubmit) form.requestSubmit(addBtn); else addBtn.click();
        });
      }
      function syncSticky(variant) {
        if (!sticky) return;
        var sp = qs('[data-sticky-price]', sticky);
        if (sp) sp.innerHTML = formatMoney(variant.price);
        stickyBtn.disabled = !variant.available;
        (qs('span', stickyBtn) || stickyBtn).textContent = variant.available ? (GMJ.strings.addToBag || 'Add to bag') : (GMJ.strings.soldOut || 'Sold out');
      }

      function selectedOptions() {
        return qsa('.vpick__opt', picker).map(function (fs) {
          var c = qs('input:checked', fs);
          return c ? c.value : null;
        });
      }
      function markAvailability(sel) {
        qsa('.vpick__opt', picker).forEach(function (fs, i) {
          qsa('.vpick__val', fs).forEach(function (lab) {
            var val = qs('input', lab).value;
            var ok = product.variants.some(function (v) {
              return v.available && v.options[i] === val && v.options.every(function (o, j) { return j === i || o === sel[j]; });
            });
            lab.classList.toggle('is-unavailable', !ok);
          });
        });
      }
      function setLabel(text) {
        if (!addBtn) return;
        (qs('span', addBtn) || addBtn).textContent = text;
      }

      if (picker) {
        markAvailability(selectedOptions());
        picker.addEventListener('change', function () {
          var sel = selectedOptions();
          sel.forEach(function (v, i) {
            var lab = qs('[data-option-label="' + i + '"]', picker);
            if (lab && v) lab.textContent = v;
          });
          markAvailability(sel);
          var variant = null;
          for (var k = 0; k < product.variants.length; k++) {
            var v = product.variants[k];
            if (v.options.every(function (o, i) { return o === sel[i]; })) { variant = v; break; }
          }
          if (!variant) {
            if (addBtn) addBtn.disabled = true;
            setLabel(GMJ.strings.unavailable || 'Unavailable');
            return;
          }
          if (idInput) idInput.value = variant.id;
          if (addBtn) addBtn.disabled = !variant.available;
          setLabel(variant.available ? (GMJ.strings.addToBag || 'Add to bag') : (GMJ.strings.soldOut || 'Sold out'));
          if (price) {
            var sale = variant.compare_at_price && variant.compare_at_price > variant.price;
            price.className = 'price price--lg' + (sale ? ' price--sale' : '') + (variant.available ? '' : ' price--sold');
            price.innerHTML = '<span class="price__now">' + formatMoney(variant.price) + '</span>' + (sale ? '<s class="price__was">' + formatMoney(variant.compare_at_price) + '</s>' : '');
          }
          syncSticky(variant);
          if (variant.featured_media) showMedia(variant.featured_media.id);
          var objNo = qs('[data-object-no]', sec);
          if (objNo && variant.sku && objNo.textContent.trim() === (product.variants[0].sku || '').trim()) objNo.textContent = variant.sku;
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          } catch (e) { /* noop */ }
        });
      }

      qsa('[data-qty-step]', sec).forEach(function (b) {
        b.addEventListener('click', function () {
          var input = qs('input', b.parentNode);
          if (!input) return;
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.getAttribute('data-qty-step')));
        });
      });

      initLoupe(sec);
    });
  }

  function initRecommendations(root) {
    qsa('[data-recommendations]', root).forEach(function (sec) {
      if (qs('.products', sec) || !sec.getAttribute('data-url')) return;
      fetch(sec.getAttribute('data-url'))
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = qs('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            sec.innerHTML = fresh.innerHTML;
            initAll(sec);
            collectRooms();
          }
        })
        .catch(function () { /* recommendations are optional */ });
    });
  }

  /* ==========================================================================
     The scribe: names in hieroglyphs
     ========================================================================== */
  var GLYPHS = {
    a: '𓄿', b: '𓃀', c: '𓎡', d: '𓂧', e: '𓇋', f: '𓆑', g: '𓎼', h: '𓉔', i: '𓇋', j: '𓆓', k: '𓎡', l: '𓃭', m: '𓅓', n: '𓈖', o: '𓍯', p: '𓊪', q: '𓈎', r: '𓂋', s: '𓋴', t: '𓏏', u: '𓅱', v: '𓆑', w: '𓅱', x: '𓎡𓋴', y: '𓇌', z: '𓊃',
    sh: '𓈙', kh: '𓐍', ch: '𓍿', th: '𓏏',
    'ا': '𓄿', 'أ': '𓄿', 'إ': '𓇋', 'آ': '𓄿', 'ء': '𓄿', 'ب': '𓃀', 'ت': '𓏏', 'ث': '𓏏', 'ج': '𓆓', 'ح': '𓎛', 'خ': '𓐍', 'د': '𓂧', 'ذ': '𓂧', 'ر': '𓂋', 'ز': '𓊃', 'س': '𓋴', 'ش': '𓈙', 'ص': '𓋴', 'ض': '𓂧', 'ط': '𓏏', 'ظ': '𓊃', 'ع': '𓂝', 'غ': '𓎼', 'ف': '𓆑', 'ق': '𓈎', 'ك': '𓎡', 'ل': '𓃭', 'م': '𓅓', 'ن': '𓈖', 'ه': '𓉔', 'ة': '𓏏', 'و': '𓅱', 'ي': '𓇌', 'ى': '𓇌'
  };
  var GLYPH_NAMES = { '𓄿': 'vulture', '𓃀': 'foot', '𓎡': 'basket', '𓂧': 'hand', '𓇋': 'reed', '𓆑': 'viper', '𓎼': 'jar stand', '𓉔': 'shelter', '𓆓': 'cobra', '𓃭': 'lion', '𓅓': 'owl', '𓈖': 'water', '𓍯': 'lasso', '𓊪': 'stool', '𓈎': 'hill', '𓂋': 'mouth', '𓋴': 'cloth', '𓏏': 'bread', '𓅱': 'quail chick', '𓇌': 'two reeds', '𓊃': 'bolt', '𓈙': 'pool', '𓐍': 'placenta', '𓍿': 'tether', '𓎛': 'wick', '𓂝': 'arm' };

  function spell(s) {
    s = (s || '').toLowerCase();
    var res = [], i = 0;
    while (i < s.length) {
      var two = s.substr(i, 2);
      if (GLYPHS[two] && /^[a-z]{2}$/.test(two)) { res.push({ g: GLYPHS[two], l: two }); i += 2; continue; }
      var c = s[i];
      if (GLYPHS[c]) res.push({ g: GLYPHS[c], l: c });
      i++;
    }
    return res;
  }

  function initCartouche(root) {
    qsa('[data-cartouche]', root).forEach(function (sec) {
      if (sec.getAttribute('data-cartouche-on')) return;
      sec.setAttribute('data-cartouche-on', '1');
      var input = qs('[data-cartouche-input]', sec);
      var out = qs('[data-cartouche-out]', sec);
      var translit = qs('[data-cartouche-translit]', sec);
      var legend = qs('[data-cartouche-legend]', sec);
      var priceEl = qs('[data-cartouche-price]', sec);
      var stage = qs('[data-cartouche-stage]', sec);
      var metals = qsa('[data-metal]', sec);
      var current = metals[0] || null;
      if (!input || !out) return;

      function render() {
        var r = spell(input.value);
        if (!r.length) {
          out.innerHTML = '<span class="g" style="opacity:.4">𓋹</span>';
          translit.textContent = GMJ.strings.typeName || 'Type a name';
          legend.innerHTML = '';
        } else {
          out.innerHTML = r.map(function (x, i) {
            return Array.from(x.g).map(function (g) { return '<span class="g" style="animation-delay:' + (i * 60) + 'ms">' + g + '</span>'; }).join('');
          }).join('');
          translit.textContent = r.map(function (x) { return /[a-z]/.test(x.l) ? x.l.toUpperCase() : x.l; }).join(' · ');
          var seen = {};
          legend.innerHTML = r.filter(function (x) {
            if (seen[x.g] || !GLYPH_NAMES[x.g]) return false;
            seen[x.g] = 1;
            return true;
          }).map(function (x) { return '<span><b>' + x.g + '</b>' + GLYPH_NAMES[x.g] + '</span>'; }).join('');
        }
        if (priceEl) priceEl.innerHTML = current ? current.getAttribute('data-price') : '';
      }
      function setMetal(btn) {
        current = btn;
        metals.forEach(function (m) {
          m.setAttribute('aria-checked', m === btn ? 'true' : 'false');
          m.tabIndex = m === btn ? 0 : -1;
        });
        ['m1', 'm2', 'm3'].forEach(function (k) {
          var v = btn.getAttribute('data-' + k);
          if (v) stage.style.setProperty('--' + k, v);
        });
        render();
      }
      metals.forEach(function (btn, idx) {
        btn.addEventListener('click', function () { setMetal(btn); });
        btn.addEventListener('keydown', function (e) {
          var step = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          if (isRTL && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) step = -step;
          var next = metals[(idx + step + metals.length) % metals.length];
          setMetal(next);
          next.focus();
        });
      });
      input.addEventListener('input', render);
      if (current) setMetal(current); else render();

      var order = qs('[data-cartouche-order]', sec);
      if (order) order.addEventListener('click', function () {
        var name = input.value.trim();
        if (!name) { input.focus(); return; }
        var vid = current && current.getAttribute('data-variant');
        if (!vid) { toast(esc(GMJ.strings.soldOut || 'Sold out')); return; }
        order.classList.add('is-loading');
        addToCart({
          items: [{
            id: Number(vid),
            quantity: 1,
            properties: {
              Name: name,
              Hieroglyphs: spell(name).map(function (x) { return x.g; }).join(''),
              Metal: current.getAttribute('data-label') || ''
            }
          }]
        })
          .then(function () { afterAdd('“' + name + '”'); })
          .catch(cartError)
          .then(function () { order.classList.remove('is-loading'); });
      });
    });
  }

  /* ==========================================================================
     Smaller pieces: boutiques map, frieze, announcements, search, filters
     ========================================================================== */
  function initBoutiques(root) {
    qsa('[data-boutiques]', root).forEach(function (sec) {
      var els = qsa('[data-loc]', sec);
      if (!els.length) return;
      function set(key) { els.forEach(function (el) { el.classList.toggle('is-active', el.getAttribute('data-loc') === key); }); }
      els.forEach(function (el) {
        ['mouseenter', 'focus', 'click'].forEach(function (ev) {
          el.addEventListener(ev, function () { set(el.getAttribute('data-loc')); });
        });
      });
      set(els[0].getAttribute('data-loc'));
    });
  }

  function initFrieze(root) {
    qsa('[data-frieze]', root).forEach(function (track) {
      if (track.getAttribute('data-frieze-on')) return;
      track.setAttribute('data-frieze-on', '1');
      var items = Array.prototype.slice.call(track.children);
      if (!items.length) return;
      // Repeat until one copy is wider than the screen, then duplicate once for a seamless loop
      var guard = 0;
      while (track.scrollWidth < window.innerWidth * 1.1 && guard++ < 6) {
        items.forEach(function (it) { track.appendChild(cleanClone(it)); });
      }
      Array.prototype.slice.call(track.children).forEach(function (it) { track.appendChild(cleanClone(it)); });
    });
  }
  function cleanClone(node) {
    var c = node.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    Array.prototype.slice.call(c.attributes).forEach(function (a) {
      if (a.name.indexOf('data-shopify') === 0) c.removeAttribute(a.name);
    });
    return c;
  }

  function initAnnouncements(root) {
    qsa('[data-announce]', root).forEach(function (bar) {
      if (bar.getAttribute('data-announce-on')) return;
      bar.setAttribute('data-announce-on', '1');
      var items = qsa('.announce__item', bar);
      if (items.length < 2 || reduce.matches) return;
      var i = 0, paused = false;
      var ms = (Number(bar.getAttribute('data-interval')) || 5) * 1000;
      bar.addEventListener('mouseenter', function () { paused = true; });
      bar.addEventListener('mouseleave', function () { paused = false; });
      setInterval(function () {
        if (paused || !bar.isConnected) return;
        items[i].classList.remove('is-on');
        i = (i + 1) % items.length;
        items[i].classList.add('is-on');
      }, ms);
    });
  }

  function initPredictive() {
    var input = qs('[data-predictive-input]');
    var out = qs('[data-predictive-results]');
    if (!input || !out || !GMJ.routes.predictiveSearch) return;
    var timer, ctrl;
    function imgUrl(url, w) { return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(function () {
        if (ctrl && ctrl.abort) ctrl.abort();
        ctrl = window.AbortController ? new AbortController() : null;
        fetch(GMJ.routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8', ctrl ? { signal: ctrl.signal } : {})
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var items = (j.resources && j.resources.results && j.resources.results.products) || [];
            if (!items.length) {
              out.innerHTML = '<p class="predictive__none">' + esc(GMJ.strings.noResults || 'Nothing found') + '</p>';
              return;
            }
            out.innerHTML = '<div class="predictive">' + items.map(function (p, i) {
              var img = p.featured_image && p.featured_image.url ? '<img src="' + esc(imgUrl(p.featured_image.url, 160)) + '" alt="" loading="lazy">' : '<span></span>';
              var cents = Math.round(parseFloat(p.price) * 100);
              return '<a class="predictive__item" style="--i:' + i + '" href="' + esc(p.url) + '">' + img +
                '<span><b>' + esc(p.title) + '</b><span>' + formatMoney(cents) + '</span></span></a>';
            }).join('') + '</div><a class="more predictive__all" href="' + esc(GMJ.routes.search + '?type=product&q=' + encodeURIComponent(q)) + '">' + esc(GMJ.strings.viewAll || 'View all') + '</a>';
          })
          .catch(function () { /* aborted or offline */ });
      }, 220);
    });
  }

  /* ==========================================================================
     Page transitions (fallback for browsers without cross-document view transitions)
     ========================================================================== */
  function initTransitions() {
    if (!GMJ.settings.pageTransitions || reduce.matches) return;
    if ('onpagereveal' in window) return;
    d.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a[href]');
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin || /^(mailto|tel):/.test(a.getAttribute('href'))) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      html.classList.add('is-leaving');
      setTimeout(function () { window.location.href = url.href; }, 320);
    });
    window.addEventListener('pageshow', function () { html.classList.remove('is-leaving'); });
  }

  /* ==========================================================================
     Global listeners
     ========================================================================== */
  d.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      e.preventDefault();
      openOv(d.getElementById(opener.getAttribute('data-overlay-open')), opener);
      return;
    }
    var closer = e.target.closest('[data-overlay-close]');
    if (closer) {
      var ov = closer.closest('[data-overlay]');
      if (ov) closeOv(ov);
      return;
    }
    var bag = e.target.closest('[data-cart-open]');
    if (bag && GMJ.settings.cartType !== 'page' && d.getElementById('CartDrawer')) {
      e.preventDefault();
      openOv(d.getElementById('CartDrawer'), bag);
      return;
    }
    var line = e.target.closest('[data-cart-drawer-inner] [data-line][data-qty]');
    if (line) {
      e.preventDefault();
      changeLine(Number(line.getAttribute('data-line')), Number(line.getAttribute('data-qty')));
      return;
    }
    var next = e.target.closest('[data-scroll-next]');
    if (next) {
      var sec = next.closest('.shopify-section');
      var target = sec && sec.nextElementSibling;
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth' });
      }
    }
  });

  d.addEventListener('keydown', function (e) {
    if (!openOverlay) return;
    if (e.key === 'Escape') closeOv(openOverlay);
    else if (e.key === 'Tab') trapFocus(e);
  });

  d.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('[data-cart-form], form.pform')) return;
    if (GMJ.settings.cartType === 'page' || !window.fetch || !window.FormData) return;
    var missing = form.id ? qsa('[form="' + form.id + '"][required]').filter(function (el) { return !el.value.trim(); }) : [];
    if (missing.length) {
      e.preventDefault();
      missing[0].focus();
      if (missing[0].reportValidity) missing[0].reportValidity();
      return;
    }
    e.preventDefault();
    var btn = qs('[type="submit"]', form);
    if (btn) btn.classList.add('is-loading');
    addToCart(new FormData(form))
      .then(function () { markDone(btn); afterAdd(btn ? btn.getAttribute('data-title') || '' : ''); })
      .catch(cartError)
      .then(function () { if (btn) btn.classList.remove('is-loading'); });
  });

  d.addEventListener('change', function (e) {
    var el = e.target;
    var form = el.closest && el.closest('[data-filter-form]');
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit(); else form.submit();
  });

  /* ==========================================================================
     Boot
     ========================================================================== */
  function initAll(root) {
    initSplits(root);
    initReveals(root);
    initLights(root);
    initDust(root);
    initParallax(root);
    initTilt(root);
    initWalk(root);
    initSwipe(root);
    initProduct(root);
    initCartouche(root);
    initBoutiques(root);
    initFrieze(root);
    initAnnouncements(root);
    initRecommendations(root);
  }

  function boot() {
    initGate();
    initHeader();
    initAll(d);
    initTorch();
    initRooms();
    initPredictive();
    initTransitions();
    tick();
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run when a section is added, changed or re-ordered
  d.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
    collectRooms();
    tick();
  });
  d.addEventListener('shopify:section:reorder', function () { collectRooms(); tick(); });
  d.addEventListener('shopify:block:select', function (e) {
    var item = e.target;
    if (item.classList && item.classList.contains('announce__item')) {
      qsa('.announce__item', item.parentNode).forEach(function (i) { i.classList.toggle('is-on', i === item); });
    }
    if (item.classList && item.classList.contains('reveal-pending')) {
      item.classList.remove('reveal-pending');
      item.classList.add('is-in');
    }
  });

  window.GMJ.formatMoney = formatMoney;
  window.GMJ.toast = toast;
})();
